import React, { useState, useCallback, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import _ from "underscore";
import styles from "../../customDashboard.module.css";
import FinanceCard from "../../../../../components/finance/financeCard/financeCard";
import FinanceCardChart from "../../../../../components/finance/financeCardChart/financeCardChart";
import FilterSelect from "../../../../../components/predictive/filterSelect/filterSelect";
import { insightsFilters } from "../../../../../services/insights";
import { useInstitution } from "../../../../../context/InstitutionContext";

// Mapeamento de ícones
import ChartMixedPorcentageIcon from "../../../../../assets/icons/chart-mixed-porcentage.svg";
import ChartMixedUpCircleDollarIcon from "../../../../../assets/icons/chart-mixed-up-circle-dollar.svg";
import ChartMixedDownCircleDollarIcon from "../../../../../assets/icons/chart-mixed-down-circle-dollar.svg";
import ChartMoneyIcon from "../../../../../assets/icons/chart-money.svg";
import FinanceIcon from "../../../../../assets/icons/finance.svg";
import AcademicIcon from "../../../../../assets/icons/academic.svg";
import DashboardIcon from "../../../../../assets/icons/dashboard-icon.svg";
import BookIcon from "../../../../../assets/icons/book.svg";
import CourseIcon from "../../../../../assets/icons/course.svg";
import LocaleIcon from "../../../../../assets/icons/locale.svg";
import ClassesIcon from "../../../../../assets/icons/classes.svg";
import DisciplineIcon from "../../../../../assets/icons/discipline.svg";
import CalendarDayIcon from "../../../../../assets/icons/calendar-day.svg";

const ICON_MAP = {
  "chart-mixed-porcentage": ChartMixedPorcentageIcon,
  "chart-mixed-up-circle-dollar": ChartMixedUpCircleDollarIcon,
  "chart-mixed-down-circle-dollar": ChartMixedDownCircleDollarIcon,
  "chart-money": ChartMoneyIcon,
  "finance": FinanceIcon,
  "academic": AcademicIcon,
  "dashboard-icon": DashboardIcon,
  "book": BookIcon,
  "course": CourseIcon,
  "locale": LocaleIcon,
  "classes": ClassesIcon,
  "discipline": DisciplineIcon
};

const getIconByName = (iconName) => {
  return ICON_MAP[iconName] || ChartMixedDownCircleDollarIcon;
};

// Função para formatar valores baseado no labelType
const formatValueByType = (value, labelType, options = {}) => {
  const numValue = Number(value) || 0;
  
  if (labelType === "percent") {
    return new Intl.NumberFormat('pt-BR', {
      style: 'percent',
      minimumFractionDigits: options.minimumFractionDigits ?? 0,
      maximumFractionDigits: options.maximumFractionDigits ?? 2,
      ...options
    }).format(numValue / 100);
  }
  
  // Default: currency
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    ...options
  }).format(numValue);
};

// TODO: Remover quando o backend retornar dados reais para estes indicadores.
// Valores mockados temporários exibidos quando o indicador estiver presente em um dashboard.
const MOCK_INDICATOR_VALUES = [
  {
    matcher: /mensalidades?\s+inadimplent/i,
    value: 33,
    labelType: "percent"
  },
  {
    matcher: /valor\s+inadimplent/i,
    value: 1254.24,
    labelType: "currency"
  },
  {
    matcher: /taxa\s+de\s+inadimpl[eê]ncia/i,
    value: 1350.23,
    labelType: "currency"
  }
];

const getMockedValueForIndicatorName = (indicatorName) => {
  if (!indicatorName) return null;
  return MOCK_INDICATOR_VALUES.find((mock) => mock.matcher.test(indicatorName)) || null;
};

const applyMockedValueToWidget = (widget) => {
  const mock = getMockedValueForIndicatorName(widget?.indicatorName);
  if (!mock) return widget;

  return {
    ...widget,
    visualizationType: "label",
    labelType: mock.labelType,
    data: {
      ...(widget.data || {}),
      value: mock.value
    }
  };
};

// Indicadores que devem ocupar a linha inteira do grid quando renderizados.
const FULL_ROW_INDICATOR_PATTERNS = [
  /desconto\s+m[eé]dio\s+sobre\s+o\s+faturado/i
];

const shouldOccupyFullRow = (indicatorName) => {
  if (!indicatorName) return false;
  return FULL_ROW_INDICATOR_PATTERNS.some((pattern) => pattern.test(indicatorName));
};

const DashboardViewer = ({ 
  selectedDashboard, 
  dashboardData, 
  loadingDashboard,
  onFiltersChange,
  savedFilters
}) => {
  const { institution } = useInstitution();
  const filtersInitialized = useRef(false);

  // Estados dos filtros
  const [knowledgeAreas, setKnowledgeAreas] = useState([]);
  const [selectedKnowledgeArea, setSelectedKnowledgeArea] = useState("");
  const [knowledgeAreasLoading, setKnowledgeAreasLoading] = useState(false);
  const [knowledgeAreasFetched, setKnowledgeAreasFetched] = useState(false);

  const [disciplines, setDisciplines] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState("");
  const [disciplinesLoading, setDisciplinesLoading] = useState(false);
  const [disciplinesFetched, setDisciplinesFetched] = useState(false);

  const [courseTypes, setCourseTypes] = useState([]);
  const [selectedCourseType, setSelectedCourseType] = useState("");
  const [courseTypesLoading, setCourseTypesLoading] = useState(false);
  const [courseTypesFetched, setCourseTypesFetched] = useState(false);

  const [localities, setLocalities] = useState([]);
  const [selectedLocality, setSelectedLocality] = useState("");
  const [localitiesLoading, setLocalitiesLoading] = useState(false);
  const [localitiesFetched, setLocalitiesFetched] = useState(false);

  const [modalities, setModalities] = useState([]);
  const [selectedModality, setSelectedModality] = useState("");
  const [modalitiesLoading, setModalitiesLoading] = useState(false);
  const [modalitiesFetched, setModalitiesFetched] = useState(false);

  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState("");
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [shiftsFetched, setShiftsFetched] = useState(false);

  // Restaurar filtros salvos do dashboard ao abrir
  useEffect(() => {
    if (!savedFilters || filtersInitialized.current) return;
    const hasFilters = Object.values(savedFilters).some(v => v !== "");
    if (!hasFilters) return;

    filtersInitialized.current = true;
    if (savedFilters.courseType) setSelectedCourseType(savedFilters.courseType);
    if (savedFilters.knowledgeArea) setSelectedKnowledgeArea(savedFilters.knowledgeArea);
    if (savedFilters.discipline) setSelectedDiscipline(savedFilters.discipline);
    if (savedFilters.locality) setSelectedLocality(savedFilters.locality);
    if (savedFilters.modality) setSelectedModality(savedFilters.modality);
    if (savedFilters.shift) setSelectedShift(savedFilters.shift);

    // Pré-carregar as opções dos selects que têm valor salvo
    const preloadOptions = async () => {
      if (savedFilters.courseType) await fetchCourseTypes();
      if (savedFilters.knowledgeArea) await fetchKnowledgeAreas();
      if (savedFilters.discipline) await fetchDisciplines();
      if (savedFilters.locality) await fetchLocalities();
      if (savedFilters.modality) await fetchModalities();
      if (savedFilters.shift) await fetchShifts();
    };
    preloadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilters?.courseType, savedFilters?.knowledgeArea, savedFilters?.discipline, savedFilters?.locality, savedFilters?.modality, savedFilters?.shift]);

  // Resetar quando muda de dashboard
  const dashboardIdForReset = selectedDashboard?.dashboardId || selectedDashboard?.id;
  useEffect(() => {
    filtersInitialized.current = false;
  }, [dashboardIdForReset]);

  // Função debounced para notificar mudanças nos filtros
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const notifyFiltersChange = useCallback(
    _.debounce((filters) => {
      if (onFiltersChange) {
        onFiltersChange(filters);
      }
    }, 500),
    [onFiltersChange]
  );

  // Funções de fetch dos filtros
  const fetchKnowledgeAreas = async () => {
    if (knowledgeAreas.length > 0) return;
    try {
      setKnowledgeAreasLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "knowledgearea"
      });

      setKnowledgeAreas(response.filterValues || []);
      setKnowledgeAreasFetched(true);
    } catch (error) {
      console.log("Erro ao buscar áreas de conhecimento", error);
    } finally {
      setKnowledgeAreasLoading(false);
    }
  };

  const fetchDisciplines = async () => {
    if (disciplines.length > 0) return;
    try {
      setDisciplinesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        KnowledgeAreaFilter: selectedKnowledgeArea,
        predictiveFilter: "filterclass",
      });

      setDisciplines(response.filterValues || []);
      setDisciplinesFetched(true);
    } catch (error) {
      console.log("Erro ao buscar disciplinas", error);
    } finally {
      setDisciplinesLoading(false);
    }
  };

  const fetchCourseTypes = async () => {
    if (courseTypes.length > 0) return;
    try {
      setCourseTypesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        KnowledgeAreaFilter: selectedKnowledgeArea,
        filterClass: selectedDiscipline,
        predictiveFilter: "filterdegree",
      });

      setCourseTypes(response.filterValues || []);
      setCourseTypesFetched(true);
    } catch (error) {
      console.log("Erro ao buscar tipos de curso", error);
    } finally {
      setCourseTypesLoading(false);
    }
  };

  const fetchLocalities = async () => {
    if (localities.length > 0) return;
    try {
      setLocalitiesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        KnowledgeAreaFilter: selectedKnowledgeArea,
        filterClass: selectedDiscipline,
        predictiveFilter: "filterlocation",
      });

      setLocalities(response.filterValues || []);
      setLocalitiesFetched(true);
    } catch (error) {
      console.log("Erro ao buscar localidades", error);
    } finally {
      setLocalitiesLoading(false);
    }
  };

  const fetchModalities = async () => {
    if (modalities.length > 0) return;
    try {
      setModalitiesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "filtermodality",
      });

      setModalities(response.filterValues || []);
      setModalitiesFetched(true);
    } catch (error) {
      console.log("Erro ao buscar modalidades", error);
    } finally {
      setModalitiesLoading(false);
    }
  };

  const fetchShifts = async () => {
    if (shifts.length > 0) return;
    try {
      setShiftsLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "filtershift",
      });

      setShifts(response.filterValues || []);
      setShiftsFetched(true);
    } catch (error) {
      console.log("Erro ao buscar turnos", error);
    } finally {
      setShiftsLoading(false);
    }
  };

  // Handlers de mudança de filtros
  const handleKnowledgeAreaChange = (e) => {
    const newKnowledgeArea = e.target.value;

    setSelectedKnowledgeArea(newKnowledgeArea);
    setSelectedDiscipline('');
    setSelectedCourseType('');
    setSelectedLocality('');
    setSelectedModality('');
    setSelectedShift('');

    setDisciplines([]);
    setCourseTypes([]);
    setLocalities([]);
    setModalities([]);
    setShifts([]);

    setDisciplinesFetched(false);
    setCourseTypesFetched(false);
    setLocalitiesFetched(false);
    setModalitiesFetched(false);
    setShiftsFetched(false);

    notifyFiltersChange({
      knowledgeArea: newKnowledgeArea,
      discipline: '',
      courseType: '',
      locality: '',
      modality: '',
      shift: ''
    });
  };

  const handleDisciplineChange = (e) => {
    const newDiscipline = e.target.value;
    setSelectedDiscipline(newDiscipline);

    notifyFiltersChange({
      knowledgeArea: selectedKnowledgeArea,
      discipline: newDiscipline,
      courseType: selectedCourseType,
      locality: selectedLocality,
      modality: selectedModality,
      shift: selectedShift
    });
  };

  const handleCourseTypeChange = (e) => {
    const newCourseType = e.target.value;
    setSelectedCourseType(newCourseType);

    notifyFiltersChange({
      knowledgeArea: selectedKnowledgeArea,
      discipline: selectedDiscipline,
      courseType: newCourseType,
      locality: selectedLocality,
      modality: selectedModality,
      shift: selectedShift
    });
  };

  const handleLocalityChange = (e) => {
    const newLocality = e.target.value;
    setSelectedLocality(newLocality);

    notifyFiltersChange({
      knowledgeArea: selectedKnowledgeArea,
      discipline: selectedDiscipline,
      courseType: selectedCourseType,
      locality: newLocality,
      modality: selectedModality,
      shift: selectedShift
    });
  };

  const handleModalityChange = (e) => {
    const newModality = e.target.value;
    setSelectedModality(newModality);

    notifyFiltersChange({
      knowledgeArea: selectedKnowledgeArea,
      discipline: selectedDiscipline,
      courseType: selectedCourseType,
      locality: selectedLocality,
      modality: newModality,
      shift: selectedShift
    });
  };

  const handleShiftChange = (e) => {
    const newShift = e.target.value;
    setSelectedShift(newShift);

    notifyFiltersChange({
      knowledgeArea: selectedKnowledgeArea,
      discipline: selectedDiscipline,
      courseType: selectedCourseType,
      locality: selectedLocality,
      modality: selectedModality,
      shift: newShift
    });
  };

  return (
    <>
      {selectedDashboard && (
        <div className={styles.dashboardInfoBar}>
          <div className={styles.dashboardInfoContent}>
            <h2 className={styles.dashboardInfoTitle}>{selectedDashboard.name}</h2>
            <p className={styles.dashboardInfoDescription}>{selectedDashboard.description}</p>
          </div>
        </div>
      )}

      {/* Container de Filtros */}
      <div className={styles.viewerFiltersContainer}>
        <FilterSelect
          icon={CourseIcon}
          label="Tipo de Curso"
          value={selectedCourseType}
          loading={courseTypesLoading}
          options={courseTypes}
          onFocus={fetchCourseTypes}
          hasFetched={courseTypesFetched}
          onChange={handleCourseTypeChange}
        />
        <FilterSelect
          icon={BookIcon}
          label="Área de Conhecimento"
          value={selectedKnowledgeArea}
          loading={knowledgeAreasLoading}
          options={knowledgeAreas}
          onFocus={fetchKnowledgeAreas}
          hasFetched={knowledgeAreasFetched}
          onChange={handleKnowledgeAreaChange}
        />
        <FilterSelect
          icon={DisciplineIcon}
          label="Curso"
          value={selectedDiscipline}
          loading={disciplinesLoading}
          options={disciplines}
          onFocus={fetchDisciplines}
          hasFetched={disciplinesFetched}
          onChange={handleDisciplineChange}
        />
        <FilterSelect
          icon={LocaleIcon}
          label="Localidade"
          value={selectedLocality}
          loading={localitiesLoading}
          options={localities}
          onFocus={fetchLocalities}
          hasFetched={localitiesFetched}
          onChange={handleLocalityChange}
        />
        <FilterSelect
          icon={ClassesIcon}
          label="Modalidade"
          value={selectedModality}
          loading={modalitiesLoading}
          options={modalities}
          onFocus={fetchModalities}
          hasFetched={modalitiesFetched}
          onChange={handleModalityChange}
        />
        <FilterSelect
          icon={CalendarDayIcon}
          label="Turno"
          value={selectedShift}
          loading={shiftsLoading}
          options={shifts}
          onFocus={fetchShifts}
          hasFetched={shiftsFetched}
          onChange={handleShiftChange}
        />
      </div>

      <div className={styles.dashboardViewContent}>
        {loadingDashboard ? (
          <div className={styles.loadingContainer}>
            <span>Carregando dashboard...</span>
          </div>
        ) : dashboardData?.widgets?.length > 0 ? (
          <div className={styles.dashboardWidgetsGrid}>
            {dashboardData.widgets.map((rawWidget) => {
              const widget = applyMockedValueToWidget(rawWidget);
              const fullRow = shouldOccupyFullRow(widget.indicatorName);
              return (
                <div 
                  key={widget.widgetId} 
                  className={styles.dashboardWidget}
                  style={{
                    gridColumn: fullRow ? "1 / -1" : `span ${widget.layout?.width || 1}`,
                    gridRow: `span ${widget.layout?.height || 1}`
                  }}
                >
                  {widget.visualizationType === "label" && (
                    <FinanceCard
                      icon={widget.indicatorIcon ? getIconByName(widget.indicatorIcon) : null}
                      title={widget.indicatorName}
                      value={formatValueByType(widget.data?.value || 0, widget.labelType)}
                      explanation={widget.indicatorDescription}
                    />
                  )}
                  {widget.visualizationType === "time_series" && widget.data?.points && (
                    <FinanceCardChart
                      title={widget.indicatorName}
                    >
                      <Line
                        data={{
                          labels: widget.data.points.map((point) => point.x),
                          datasets: [
                            {
                              label: widget.indicatorName,
                              data: widget.data.points.map((point) => point.y),
                              borderColor: "#0962AD",
                              backgroundColor: "rgba(9, 98, 173, 0.1)",
                              fill: true,
                              tension: 0.4,
                              pointRadius: 4,
                              pointBackgroundColor: "#0962AD"
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: false
                            },
                            tooltip: {
                              callbacks: {
                                label: (context) => {
                                  return formatValueByType(context.raw, widget.labelType);
                                }
                              }
                            }
                          },
                          scales: {
                            x: {
                              grid: {
                                display: false
                              }
                            },
                            y: {
                              ticks: {
                                callback: (value) => {
                                  return formatValueByType(value, widget.labelType, { notation: 'compact' });
                                }
                              }
                            }
                          }
                        }}
                      />
                    </FinanceCardChart>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.emptyStateContainer}>
            <p>Nenhum widget configurado no dashboard.</p>
          </div>
        )}
      </div>
    </>
  );
};

export default DashboardViewer;
